import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ClipboardEdit, ExternalLink, FileText, X, AlertCircle, ArrowLeft } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";
import PdfFillEditor from "@/components/PdfFillEditor";
import { mediaSrc } from "@/lib/mediaSrc";

const isAnswered = (v) => Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && String(v).trim() !== "");

function fmtAnswer(fld, v) {
  if (!isAnswered(v)) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (fld.type === "boolean") return v === "yes" ? "Yes" : v === "no" ? "No" : String(v);
  return String(v);
}

export default function PatientForms() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const [assignments, setAssignments] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [active, setActive] = useState(null);        // structured-form modal
  const [pdfActive, setPdfActive] = useState(null);  // pdf-fill editor modal
  const [answers, setAnswers] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  const refresh = () => {
    api.get("/form-assignments/mine")
      .then((r) => setAssignments(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load your forms"));
    api.get("/patient/pending-intakes")
      .then((r) => setIntakes(r.data))
      .catch(() => setIntakes([]));
  };
  useEffect(() => { refresh(); }, []);

  const openForm = (a) => { try { window.dispatchEvent(new Event("fl:close-onboarding")); } catch {} setActive(a); setAnswers({}); setReviewing(false); setMissing([]); };
  const closeForm = () => { setActive(null); setReviewing(false); setMissing([]); };
  const openPdfEditor = (a) => { try { window.dispatchEvent(new Event("fl:close-onboarding")); } catch {} setPdfActive(a); };
  const closePdfEditor = () => setPdfActive(null);
  const formDialogRef = useModalA11y({ open: !!active, onClose: closeForm });
  const pdfDialogRef = useModalA11y({ open: !!pdfActive, onClose: closePdfEditor });

  const outstanding = useMemo(() => assignments.filter((a) => a.status !== "completed"), [assignments]);
  const done = useMemo(() => assignments.filter((a) => a.status === "completed"), [assignments]);

  const openAssignment = (a) => {
    const form = a.form || {};
    const digitalReady = (Array.isArray(form.fields) && form.fields.length > 0)
      || (Array.isArray(form.parsed_fields) && form.parsed_fields.length > 0);
    const isPdfOnly = form.kind === "pdf" && !digitalReady;
    const isAnkle = form.kind === "ankle_finder" || a.form_kind === "ankle_finder";
    if (isAnkle) return navigate(`/online-programmes/ankle/finder?assignment=${a.id}`);
    if (isPdfOnly) return openPdfEditor(a);
    return openForm(a);
  };

  // Deep-link: /app/forms/:assignmentId opens the exact assigned form.
  useEffect(() => {
    if (autoOpened || !assignmentId || assignments.length === 0) return;
    const a = assignments.find((x) => x.id === assignmentId);
    if (a) {
      setAutoOpened(true);
      if (a.status === "completed") toast.message("You've already completed this form");
      else openAssignment(a);
    }
    // eslint-disable-next-line
  }, [assignmentId, assignments, autoOpened]);

  const fields = active?.form?.fields || [];

  const handlePrimary = async () => {
    if (!reviewing) {
      const miss = fields.filter((f) => f.required && !isAnswered(answers[f.key])).map((f) => f.key);
      setMissing(miss);
      if (miss.length > 0) { toast.error(`Please answer the ${miss.length} required question${miss.length > 1 ? "s" : ""} highlighted below`); return; }
      setReviewing(true);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/form-responses", { assignment_id: active.id, answers });
      toast.success("Form submitted — your clinician will see it");
      closeForm(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const openFormPdf = async (fid) => {
    try {
      const src = await mediaSrc("form", fid);
      window.open(src, "_blank", "noopener,noreferrer");
    } catch { toast.error("Could not open PDF"); }
  };

  const renderField = (fld) => {
    const val = answers[fld.key] || "";
    const set = (v) => setAnswers({ ...answers, [fld.key]: v });
    const base = "fl-input min-h-[44px]";
    if (fld.type === "textarea") return <textarea className={base} rows={3} value={val} onChange={(e) => set(e.target.value)} data-testid={`ptf-field-${fld.key}`} />;
    if (fld.type === "number" || fld.type === "scale") return <input type="number" inputMode="decimal" className={base} min={fld.min} max={fld.max} value={val} onChange={(e) => set(e.target.value)} data-testid={`ptf-field-${fld.key}`} />;
    if (fld.type === "select") return (
      <select className={base} value={val} onChange={(e) => set(e.target.value)} data-testid={`ptf-field-${fld.key}`}>
        <option value="">— choose —</option>
        {(fld.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    if (fld.type === "checkbox") {
      const arr = Array.isArray(val) ? val : [];
      const toggle = (o) => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      return (
        <div className="flex flex-col gap-2" data-testid={`ptf-field-${fld.key}`}>
          {(fld.options || []).map((o) => {
            const on = arr.includes(o);
            return (
              <button key={o} type="button" onClick={() => toggle(o)}
                className={`flex items-center gap-2 min-h-[44px] px-3 rounded-md text-sm text-left border transition-colors ${on ? "bg-[#f47a20]/15 border-[#f47a20] text-white" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>
                <span className={`w-4 h-4 rounded-sm border grid place-items-center shrink-0 ${on ? "bg-[#f47a20] border-[#f47a20]" : "border-white/30"}`}>{on && <CheckCircle2 size={12} className="text-black" />}</span>
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    if (fld.type === "boolean") return (
      <div className="flex gap-2" data-testid={`ptf-field-${fld.key}`}>
        {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => set(v)}
            className={`flex-1 min-h-[44px] rounded-md text-sm border transition-colors ${val === v ? "bg-[#f47a20] text-black border-[#f47a20]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>
            {l}
          </button>
        ))}
      </div>
    );
    if (fld.type === "date") return <input type="date" className={base} value={val} onChange={(e) => set(e.target.value)} data-testid={`ptf-field-${fld.key}`} />;
    return <input className={base} value={val} onChange={(e) => set(e.target.value)} data-testid={`ptf-field-${fld.key}`} />;
  };

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.forms.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.forms.h1")}</h1>
      </div>

      {/* Pre-visit intake — part of the clinical record, kept separate from assigned forms */}
      {intakes.length > 0 && (
        <div className="mb-8" data-testid="patient-intake-section">
          <div className="fl-heading text-xl mb-3">Pre-visit intake</div>
          <div className="grid md:grid-cols-2 gap-3">
            {intakes.map((it) => (
              <Link key={`intake-${it.id}`} to={`/app/intake/${it.id}`}
                className="fl-card p-5 border-[#f47a20]/40 hover:translate-y-[-1px] transition-transform" data-testid={`patient-intake-${it.id}`}>
                <div className="flex items-start gap-3">
                  <ClipboardEdit className="text-[#f47a20]" size={20} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="fl-heading text-lg">Intake for Initial Assessment</div>
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,69,0,.15)", color: "#f47a20", border: "1px solid rgba(255,69,0,.35)" }}>Pre-visit</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">Assigned by {it.clinician_name || "your clinician"} · {new Date(it.created_at).toLocaleDateString("en-GB")}</div>
                    <div className="text-xs text-zinc-400 mt-2">Please complete before your appointment so your clinician has the full picture.</div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2"><span className="fl-btn text-xs">Fill in intake</span></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <div className="fl-heading text-xl mb-3">Forms to complete ({outstanding.length})</div>
        {outstanding.length === 0 && <div className="fl-card p-6 text-sm text-zinc-500" data-testid="patient-forms-empty">Nothing to complete right now. Nice work.</div>}
        <div className="grid md:grid-cols-2 gap-3">
          {outstanding.map((a) => {
            const form = a.form || {};
            const digitalReady = (Array.isArray(form.fields) && form.fields.length > 0) || (Array.isArray(form.parsed_fields) && form.parsed_fields.length > 0);
            const isPdfOnly = form.kind === "pdf" && !digitalReady;
            const due = a.due_by ? new Date(a.due_by) : null;
            return (
              <div key={a.id} className="fl-card fl-card-elevated p-5 border-[#f47a20]/30 cursor-pointer hover:translate-y-[-1px] transition-transform"
                onClick={() => openAssignment(a)} role="button" tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openAssignment(a)}
                data-testid={`patient-form-${a.id}`} data-form-assignment-id={a.id}>
                <div className="flex items-start gap-3">
                  {isPdfOnly ? <FileText className="text-[#f47a20]" size={20} /> : <ClipboardEdit className="text-[#f47a20]" size={20} />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="fl-heading text-lg">{form.title || a.form_title}</div>
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,69,0,.15)", color: "#f47a20", border: "1px solid rgba(255,69,0,.35)" }} data-testid={`patient-form-badge-${a.id}`}>To complete</span>
                      {due && <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full text-zinc-300" style={{ border: "1px solid rgba(255,255,255,.15)" }}>Due {due.toLocaleDateString("en-GB")}</span>}
                    </div>
                    {form.description && <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>{form.description}</div>}
                    <div className="text-[11px] mt-2" style={{ color: "var(--fl-muted-2)" }}>Assigned by {a.clinician_name} · {new Date(a.created_at).toLocaleDateString("en-GB")}</div>
                    {a.notes && <div className="text-xs mt-2" style={{ color: "var(--fl-muted)" }}>{a.notes}</div>}
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  {isPdfOnly
                    ? <span className="fl-btn text-xs" data-testid={`patient-form-open-${a.id}`}><FileText size={12} /> Open & fill PDF</span>
                    : <span className="fl-btn text-xs" data-testid={`patient-form-open-${a.id}`}>Fill in</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {done.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowCompleted((s) => !s)} aria-expanded={showCompleted}
            data-testid="patient-forms-completed-toggle" className="w-full flex items-center justify-between gap-3 fl-card p-3 hover:bg-white/5 transition-colors">
            <span className="fl-heading text-base flex items-center gap-2"><CheckCircle2 className="text-green-500" size={16} /> Completed forms ({done.length})</span>
            <span className="text-xs" style={{ color: "var(--fl-orange)" }}>{showCompleted ? "Hide completed" : "Show completed"}</span>
          </button>
          {showCompleted && (
            <div className="grid md:grid-cols-2 gap-3 mt-3" data-testid="patient-forms-completed-list">
              {done.map((a) => (
                <div key={a.id} className="fl-card p-4 opacity-60">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="text-green-500" size={16} />
                    <div className="flex-1">
                      <div className="text-sm">{a.form?.title || a.form_title}</div>
                      <div className="text-[10px] text-zinc-500">Submitted {a.completed_at ? new Date(a.completed_at).toLocaleDateString("en-GB") : "—"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 md:p-6 overflow-auto" onClick={closeForm}>
          <div ref={formDialogRef} role="dialog" aria-modal="true" aria-labelledby="pt-form-title"
            className="fl-card fl-card-elevated p-5 md:p-6 w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div id="pt-form-title" className="fl-heading text-2xl">{active.form?.title}</div>
                {active.form?.description && <div className="text-sm text-zinc-400 mt-1">{active.form.description}</div>}
                {active.form?.kind === "pdf" && (
                  <button onClick={() => openFormPdf(active.form_id)} className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-[color:var(--fl-orange)] mt-2" data-testid="pt-form-original-pdf">
                    <ExternalLink size={11} /> View the original PDF for reference
                  </button>
                )}
              </div>
              <button onClick={closeForm} aria-label="Close form"><X size={16} className="text-zinc-400" /></button>
            </div>

            {!reviewing ? (
              <div className="space-y-4 mt-6" data-testid="pt-form-edit">
                {fields.map((fld) => {
                  const isMissing = missing.includes(fld.key);
                  return (
                    <div key={fld.key} className={isMissing ? "rounded-md p-2 -m-2" : ""} style={isMissing ? { border: "1px solid rgba(239,68,68,.6)", background: "rgba(239,68,68,.06)" } : {}}>
                      <div className="fl-label mb-1">{fld.label} {fld.required && <span className="text-[#f47a20]">*</span>}</div>
                      {renderField(fld)}
                      {fld.help && <div className="text-[11px] text-zinc-500 mt-1">{fld.help}</div>}
                      {isMissing && <div className="text-[11px] text-red-400 mt-1 flex items-center gap-1"><AlertCircle size={11} /> This question is required</div>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-6" data-testid="pt-form-review">
                <div className="rounded-md p-3 mb-4 text-sm" style={{ background: "rgba(255,69,0,.08)", border: "1px solid rgba(255,69,0,.3)", color: "#f8c7a8" }}>
                  <div className="font-semibold">Review your answers</div>
                  <div className="text-xs mt-1 text-zinc-300">Please check your responses before submitting. Once submitted, your clinician will be able to review them.</div>
                </div>
                <div className="space-y-3">
                  {fields.map((fld) => (
                    <div key={fld.key} className="flex flex-col gap-0.5">
                      <div className="text-xs uppercase tracking-wider" style={{ color: "var(--fl-muted-2)" }}>{fld.label}</div>
                      <div className="text-sm text-white" data-testid={`pt-review-${fld.key}`}>{fmtAnswer(fld, answers[fld.key])}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              {reviewing && (
                <button className="fl-btn-ghost inline-flex items-center gap-1" onClick={() => setReviewing(false)} data-testid="pt-form-back"><ArrowLeft size={14} /> Go back and edit</button>
              )}
              <button className="fl-btn" onClick={handlePrimary} disabled={submitting} data-testid={reviewing ? "pt-form-submit" : "pt-form-review-btn"}>
                {reviewing ? (submitting ? "Submitting…" : "Submit form") : "Review answers"}
              </button>
              {!reviewing && <button className="fl-btn-ghost" onClick={closeForm}>Cancel</button>}
            </div>
          </div>
        </div>
      )}

      {pdfActive && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-3 overflow-auto" onClick={closePdfEditor}>
          <div ref={pdfDialogRef} role="dialog" aria-modal="true" aria-labelledby="pt-pdf-title"
            className="fl-card fl-card-elevated p-4 md:p-6 w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()} data-testid="pdf-fill-modal">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0 flex-1">
                <div id="pt-pdf-title" className="fl-heading text-xl md:text-2xl truncate">{pdfActive.form?.title || pdfActive.form_title}</div>
                <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>Fill the fields, add notes or a signature, then Save → Send to clinician.</div>
              </div>
              <button onClick={closePdfEditor} aria-label="Close" data-testid="pt-pdf-close"><X size={16} className="text-zinc-400" /></button>
            </div>
            <PdfFillEditor assignment={pdfActive} onDone={() => { refresh(); }} />
          </div>
        </div>
      )}
    </DashLayout>
  );
}
